import { PAYLINES } from '../core/paylines';
import { EXTREME_REEL_STRIPS, REEL_STRIPS } from '../core/reel-strips';
import type {
  Grid,
  GridPosition,
  Payline,
  ReelStops,
  ReelSymbolId,
  RegularSymbolId,
  RowIndex,
} from '../core/types';
import type { SpinPresentationResult } from '../rendering/types';
import type { EgyptianGodEvent } from '../bonus/types';
import type {
  ReplayLineWinV2,
  ReplayPotOfGreedV2,
  ReplayStepV2,
} from '../replay/schema';
import { MAX_REPLAY_STEPS } from '../replay/schema';
import { CycleJournal, type ValueDecoder } from '../services/cycle-journal';
import { decodeSessionV2, type PersistedSessionV2 } from '../services/persistence';
import type { StoragePort } from '../services/storage';

export interface GameCycleOutcomeV1 {
  readonly version: 1;
  readonly kind: 'base' | 'bonus';
  readonly presentation: SpinPresentationResult;
}

type UnknownRecord = Record<string, unknown>;

interface DecodedSpinPresentation {
  readonly grid: Grid;
  readonly stops: ReelStops;
  readonly scatterCount: number;
  readonly lineWins: readonly ReplayLineWinV2[];
  readonly totalWinCents: number;
  readonly stickyBefore: readonly GridPosition[];
  readonly stickyAfter: readonly GridPosition[];
  readonly newlySticky: readonly GridPosition[];
  readonly retriggerAwarded?: number;
  readonly potOfGreed?: ReplayPotOfGreedV2;
  readonly godEvents?: readonly EgyptianGodEvent[];
}

const SYMBOLS = new Set<ReelSymbolId>([
  'BEWD', 'DM', 'JINZO', 'TW', 'ACE', 'KING', 'QUEEN', 'JACK', 'WILD', 'SCATTER',
]);
const PAYING_SYMBOLS = new Set<RegularSymbolId | 'WILD'>([
  'BEWD', 'DM', 'JINZO', 'TW', 'ACE', 'KING', 'QUEEN', 'JACK', 'WILD',
]);

function record(value: unknown): UnknownRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as UnknownRecord
    : null;
}

function integer(value: unknown, min = 0, max = Number.MAX_SAFE_INTEGER): number | null {
  return Number.isSafeInteger(value) && Number(value) >= min && Number(value) <= max
    ? Number(value)
    : null;
}

function decodeGrid(value: unknown): Grid | null {
  if (!Array.isArray(value) || value.length !== 5) return null;
  const columns: ReelSymbolId[][] = [];
  for (const candidate of value) {
    if (!Array.isArray(candidate) || candidate.length !== 3
      || candidate.some((symbol) => !SYMBOLS.has(symbol as ReelSymbolId))) return null;
    const entries: readonly unknown[] = candidate;
    columns.push([
      entries[0] as ReelSymbolId,
      entries[1] as ReelSymbolId,
      entries[2] as ReelSymbolId,
    ]);
  }
  return columns as unknown as Grid;
}

function decodeStops(value: unknown, maxStops: readonly number[]): ReelStops | null {
  if (!Array.isArray(value) || value.length !== 5) return null;
  const result = value.map((stop, reel) => integer(stop, 0, maxStops[reel] ?? -1));
  return result.some((stop) => stop === null) ? null : result as unknown as ReelStops;
}

const STANDARD_STOP_MAXIMA = REEL_STRIPS.map((strip) => strip.length - 1);
const BASE_STOP_MAXIMA = EXTREME_REEL_STRIPS.map((strip, reel) => (
  Math.max(strip.length, REEL_STRIPS[reel]?.length ?? 0) - 1
));

function decodePosition(value: unknown): GridPosition | null {
  const source = record(value);
  const reel = integer(source?.reel, 0, 4);
  const row = integer(source?.row, 0, 2);
  const multiplier = source?.multiplier === undefined ? undefined : integer(source.multiplier, 1);
  return reel === null || row === null || multiplier === null ? null : {
    reel, row, ...(multiplier === undefined ? {} : { multiplier }),
  } as GridPosition;
}

function decodeGodEvents(value: unknown): readonly EgyptianGodEvent[] | undefined | null {
  if (value === undefined || value === null) return undefined;
  if (!Array.isArray(value) || value.length > 3) return null;
  const result: EgyptianGodEvent[] = [];
  for (const candidate of value) {
    const source = record(candidate);
    if (!source || !['RA', 'SLIFER', 'OBELISK'].includes(String(source.god))) return null;
    const order = integer(source.order, 1, 3);
    if (order === null) return null;
    result.push(candidate as EgyptianGodEvent);
  }
  return Object.freeze(result.sort((a, b) => a.order - b.order));
}

function decodePositions(value: unknown): readonly GridPosition[] | null {
  if (!Array.isArray(value) || value.length > 15) return null;
  const unique = new Map<string, GridPosition>();
  for (const candidate of value) {
    const position = decodePosition(candidate);
    if (position === null) return null;
    unique.set(`${position.reel}:${position.row}`, position);
  }
  if (unique.size !== value.length) return null;
  return [...unique.values()].sort((left, right) => left.reel - right.reel || left.row - right.row);
}

function decodeRoute(value: unknown, lineIndex: number): Payline | null {
  if (!Array.isArray(value) || value.length !== 5
    || value.some((row) => integer(row, 0, 2) === null)) return null;
  const expected = PAYLINES[lineIndex];
  if (expected === undefined || value.some((row, reel) => row !== expected[reel])) return null;
  const rows: readonly unknown[] = value;
  return [
    rows[0] as RowIndex,
    rows[1] as RowIndex,
    rows[2] as RowIndex,
    rows[3] as RowIndex,
    rows[4] as RowIndex,
  ];
}

function decodeLineWins(value: unknown): readonly ReplayLineWinV2[] | null {
  if (!Array.isArray(value) || value.length > 10) return null;
  const result: ReplayLineWinV2[] = [];
  for (const candidate of value) {
    const source = record(candidate);
    if (source === null) return null;
    const lineIndex = integer(source.lineIndex, 0, 9);
    const count = integer(source.count, 3, 5);
    const winCents = integer(source.winCents, 0);
    const symbolId = source.symbolId as RegularSymbolId | 'WILD';
    if (lineIndex === null || count === null || winCents === null || !PAYING_SYMBOLS.has(symbolId)) return null;
    const route = decodeRoute(source.route, lineIndex);
    if (route === null) return null;
    result.push({ lineIndex, route, count: count as 3 | 4 | 5, symbolId, winCents });
  }
  return new Set(result.map((line) => line.lineIndex)).size === result.length ? result : null;
}

function decodePot(value: unknown): ReplayPotOfGreedV2 | undefined | null {
  if (value === undefined || value === null) return undefined;
  const source = record(value);
  const position = decodePosition(source?.position);
  if (source === null || position === null || source.awardedFreeSpins !== 1
    || source.transformedToWild !== true) return null;
  return { position, awardedFreeSpins: 1, transformedToWild: true };
}

function positionKey(position: GridPosition): string {
  return `${position.reel}:${position.row}`;
}

function samePositionSequence(
  left: readonly GridPosition[],
  right: readonly GridPosition[],
): boolean {
  return left.length === right.length
    && left.every((position, index) => positionKey(position) === positionKey(right[index]!));
}

function countEvaluatedScatters(
  grid: Grid,
  stickyBefore: readonly GridPosition[],
  potOfGreed: ReplayPotOfGreedV2 | undefined,
): number {
  // The persisted grid contains the physical reel stops. Existing sticky
  // wilds (and the legacy POG overlay) replace those cells before scatter
  // evaluation, so a covered physical SCATTER must not count as visible.
  const blockers = new Set(stickyBefore.map(positionKey));
  if (potOfGreed !== undefined) blockers.add(positionKey(potOfGreed.position));
  let count = 0;
  grid.forEach((column, reel) => column.forEach((symbol, row) => {
    if (symbol === 'SCATTER' && !blockers.has(`${reel}:${row}`)) count += 1;
  }));
  return count;
}

function decodePresentation(
  value: unknown,
  totalKey: 'totalWinCents' | 'spinWinCents',
  maxStops: readonly number[],
): DecodedSpinPresentation | null {
  const source = record(value);
  if (source === null) return null;
  const grid = decodeGrid(source.grid);
  const stops = decodeStops(source.stops, maxStops);
  const scatterCount = integer(source.scatterCount, 0, 5);
  const lineWins = decodeLineWins(source.lineWins);
  const totalWinCents = integer(source[totalKey], 0);
  const stickyBefore = decodePositions(source.stickyBefore);
  const stickyAfter = decodePositions(source.stickyAfter);
  const newlySticky = decodePositions(source.newlySticky);
  const retriggerAwarded = source.retriggerAwarded === undefined
    ? undefined
    : integer(source.retriggerAwarded, 0, 12);
  const potOfGreed = decodePot(source.potOfGreed);
  const godEvents = decodeGodEvents(source.godEvents);
  if (grid === null || stops === null || scatterCount === null || lineWins === null || totalWinCents === null
    || stickyBefore === null || stickyAfter === null || newlySticky === null || retriggerAwarded === null
    || potOfGreed === null || godEvents === null) return null;
  if (countEvaluatedScatters(grid, stickyBefore, potOfGreed) !== scatterCount) return null;
  if (lineWins.reduce((sum, line) => sum + line.winCents, 0) !== totalWinCents) return null;
  if (potOfGreed !== undefined && !stickyAfter.some((position) => positionKey(position) === positionKey(potOfGreed.position))) {
    return null;
  }
  return {
    grid,
    stops,
    scatterCount,
    lineWins,
    totalWinCents,
    stickyBefore,
    stickyAfter,
    newlySticky,
    ...(retriggerAwarded === undefined || retriggerAwarded === 0 ? {} : { retriggerAwarded }),
    ...(potOfGreed === undefined ? {} : { potOfGreed }),
    ...(godEvents === undefined ? {} : { godEvents }),
  };
}

export const gameSessionDecoder: ValueDecoder<PersistedSessionV2> = Object.freeze({
  decode: decodeSessionV2,
});

export const gameCycleOutcomeDecoder: ValueDecoder<GameCycleOutcomeV1> = Object.freeze({
  decode(value: unknown): GameCycleOutcomeV1 | null {
    const source = record(value);
    if (source === null || source.version !== 1 || (source.kind !== 'base' && source.kind !== 'bonus')) return null;
    const presentation = decodePresentation(
      source.presentation,
      'totalWinCents',
      source.kind === 'base' ? BASE_STOP_MAXIMA : STANDARD_STOP_MAXIMA,
    );
    if (presentation === null) return null;
    if (source.kind === 'base' && (
      presentation.stickyBefore.length > 0
      || presentation.stickyAfter.length > 0
      || presentation.newlySticky.length > 0
      || presentation.retriggerAwarded !== undefined
      || presentation.potOfGreed !== undefined
      || presentation.godEvents !== undefined
    )) return null;
    if (source.kind === 'bonus') {
      const before = new Set(presentation.stickyBefore.map(positionKey));
      const potKey = presentation.potOfGreed === undefined
        ? null
        : positionKey(presentation.potOfGreed.position);
      const expectedNew = presentation.stickyAfter.filter((position) => (
        !before.has(positionKey(position)) && positionKey(position) !== potKey
        && !presentation.godEvents?.some((event) => event.sliferTargets?.some((target) => (
          target.previousMultiplier === 0 && positionKey(target) === positionKey(position)
        )))
      ));
      // `newlySticky` records the naturally landed WILD cells. Slifer may
      // upgrade one of those cells in the same spin, so stickyAfter can carry
      // a higher multiplier while both collections still describe the same
      // newly captured positions.
      if (!samePositionSequence(expectedNew, presentation.newlySticky)) return null;
    }
    return { version: 1, kind: source.kind, presentation };
  },
});

export const replayStepCycleDecoder: ValueDecoder<ReplayStepV2> = Object.freeze({
  decode(value: unknown): ReplayStepV2 | null {
    const source = record(value);
    if (source === null) return null;
    const presentation = decodePresentation(source, 'spinWinCents', STANDARD_STOP_MAXIMA);
    const spinIndex = integer(source.spinIndex, 1, MAX_REPLAY_STEPS);
    const cumulativeWinCents = integer(source.cumulativeWinCents, 0);
    const freeSpinsLeftAfter = integer(source.freeSpinsLeftAfter, 0, MAX_REPLAY_STEPS);
    const freeSpinsTotalAfter = integer(source.freeSpinsTotalAfter, 1, MAX_REPLAY_STEPS);
    if (presentation === null || spinIndex === null || cumulativeWinCents === null
      || freeSpinsLeftAfter === null || freeSpinsTotalAfter === null
      || cumulativeWinCents < presentation.totalWinCents || freeSpinsLeftAfter > freeSpinsTotalAfter) return null;
    const before = new Set(presentation.stickyBefore.map(positionKey));
    const potKey = presentation.potOfGreed === undefined
      ? null
      : positionKey(presentation.potOfGreed.position);
    const expectedNew = presentation.stickyAfter.filter((position) => (
      !before.has(positionKey(position)) && positionKey(position) !== potKey
      && !presentation.godEvents?.some((event) => event.sliferTargets?.some((target) => (
        target.previousMultiplier === 0 && positionKey(target) === positionKey(position)
      )))
    ));
    if (!samePositionSequence(expectedNew, presentation.newlySticky)) return null;
    return {
      spinIndex,
      stops: presentation.stops,
      grid: presentation.grid,
      lineWins: presentation.lineWins,
      spinWinCents: presentation.totalWinCents,
      cumulativeWinCents,
      scatterCount: presentation.scatterCount,
      retriggerAwarded: presentation.retriggerAwarded ?? 0,
      freeSpinsLeftAfter,
      freeSpinsTotalAfter,
      stickyBefore: presentation.stickyBefore,
      stickyAfter: presentation.stickyAfter,
      newlySticky: presentation.newlySticky,
      ...(presentation.potOfGreed === undefined ? {} : { potOfGreed: presentation.potOfGreed }),
      ...(presentation.godEvents === undefined ? {} : { godEvents: presentation.godEvents }),
    };
  },
});

export function createGameCycleJournal(
  storage: StoragePort,
): CycleJournal<PersistedSessionV2, GameCycleOutcomeV1, ReplayStepV2> {
  return new CycleJournal(
    storage,
    gameSessionDecoder,
    gameCycleOutcomeDecoder,
    replayStepCycleDecoder,
  );
}
